import React, { useState, useEffect, useCallback } from "react";
import { Download, History, Search } from "lucide-react";
import { SecurityAuditLogItem } from "./types";

export const SecurityAuditPage: React.FC = () => {
  const [logs, setLogs] = useState<SecurityAuditLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchAuditLogs = useCallback(async (searchQuery?: string) => {
    setLoading(true);
    const token = localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken");
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    try {
      let url = "/api/auth/audit-logs/";
      if (searchQuery) url += `?search=${encodeURIComponent(searchQuery)}`;
      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        const results = Array.isArray(data) ? data : data.results || [];
        const mapped: SecurityAuditLogItem[] = results.map((l: {
          id: string;
          event_type?: string;
          user_email?: string;
          ip_address?: string;
          details?: Record<string, unknown>;
          timestamp?: string;
          tenant_schema?: string;
        }) => ({
          id: l.id,
          timestamp: l.timestamp ? new Date(l.timestamp).toLocaleString() : "Just now",
          actor: l.user_email || "System User",
          action: (l.event_type || "SECURITY_EVENT").toUpperCase().replace(/_/g, " "),
          target: l.details && Object.keys(l.details).length > 0 ? JSON.stringify(l.details) : `Tenant: ${l.tenant_schema || "public"}`,
          ipAddress: l.ip_address || "127.0.0.1",
          status: "Success",
        }));
        setLogs(mapped);
      }
    } catch (err) {
      console.warn("[SecurityAudit] Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAuditLogs(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, fetchAuditLogs]);

  const handleExportCSV = () => {
    const csvHeader = "Timestamp,Actor,Action,Target,IP Address,Status\n";
    const csvRows = logs
      .map((l) => `"${l.timestamp}","${l.actor}","${l.action}","${l.target.replace(/"/g, '""')}","${l.ipAddress}","${l.status}"`)
      .join("\n");
    const blob = new Blob([csvHeader + csvRows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `security_audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="p-5 bg-slate-900/90 border border-slate-800 rounded-xl shadow-md text-xs font-sans space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-indigo-400" />
          <h2 className="text-base font-bold text-slate-100">Immutable Security Audit Logs & Access History</h2>
        </div>
        <button
          onClick={handleExportCSV}
          disabled={logs.length === 0}
          className="flex items-center gap-1.5 px-3.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold rounded-lg transition-colors text-xs disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          <span>Export Audit Log</span>
        </button>
      </div>

      {/* Search Input */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter security events by actor email, action, IP address, or tenant..."
          className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-200 text-xs font-medium focus:outline-none focus:border-indigo-500"
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-800 bg-slate-950">
        <table className="w-full text-left text-xs text-slate-300">
          <thead className="bg-slate-900 text-[10px] font-mono uppercase text-slate-400 border-b border-slate-800">
            <tr>
              <th className="p-3">Timestamp</th>
              <th className="p-3">Actor / User</th>
              <th className="p-3">Security Action</th>
              <th className="p-3">Target Resource</th>
              <th className="p-3">IP Address</th>
              <th className="p-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800 font-mono">
            {loading ? (
              <tr>
                <td colSpan={6} className="p-6 text-center text-slate-500">Loading security audit log entries...</td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-6 text-center text-slate-500">No security audit events recorded matching your query.</td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id} className="hover:bg-slate-850">
                  <td className="p-3 text-slate-400">{log.timestamp}</td>
                  <td className="p-3 font-sans font-bold text-slate-100">{log.actor}</td>
                  <td className="p-3 font-bold text-indigo-400">{log.action}</td>
                  <td className="p-3 text-slate-300 font-sans truncate max-w-xs">{log.target}</td>
                  <td className="p-3 text-slate-400">{log.ipAddress}</td>
                  <td className="p-3">
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                        log.status === "Success"
                          ? "bg-emerald-950 text-emerald-300 border border-emerald-800"
                          : "bg-rose-950 text-rose-300 border border-rose-800"
                      }`}
                    >
                      {log.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
